import React from 'react';
import ReactMarkdown from "react-markdown";

/**
 * A styled checkbox component for displaying goals and competencies.
 * It is for display purposes and is not interactive.
 * @param {object} props - The component props.
 * @param {string} props.label - The text label for the checkbox.
 * @param {boolean} [props.checked=false] - Determines if the checkbox should appear checked.
 */
const CheckboxItem = ({ label, checked = false }) => (
    <div className="flex items-center text-xs">
        <div
            className="w-3.5 h-3.5 border border-gray-500 mr-1 flex-shrink-0 bg-white flex items-center justify-center">
            {checked && <div className="w-2.5 h-2.5 bg-gray-700"></div>}
        </div>
        <span className="leading-tight">{label}</span>
    </div>
);

/**
 * This component renders the domain page for Part B of the Middle Stage HPC report.
 * It is designed to exactly match the reference image layout and styling with proper spacing.
 */
const PartBDomainPage = ({ domain, assignment }) => {
    // Extract the data properly from the API structure
    const domainData = domain || { domain_name: "DOMAIN" };

    // Handle the correct data structure
    let curricular_goals = [];
    let competencies = [];
    let activity = "";
    let assessment_questions = [];
    let assessment_rubric = [];

    // Safely extract data from assignment
    if (assignment) {
        curricular_goals = assignment.curricular_goals || [];
        competencies = assignment.competencies || [];
        activity = assignment.activity || "";
        assessment_questions = assignment.assessment_questions || [];
        assessment_rubric = assignment.assessment_rubric || [];
    }

    // Join assessment questions with line breaks
    const assessment_questions_text = assessment_questions.join('\n\n');

    // Use actual curricular goals and competencies from the assignment data
    // Create a comprehensive list that includes the actual data plus common ones for display
    const allCurricular = ["CG-1", "CG-2", "CG-3", "CG-4", "CG-5", "CG-6", "CG-7", "CG-8", "CG-9", "CG-10", "CG-11", "CG-12", "CG-13"];
    const allCompetencies = [
        "C-1.1", "C-1.2", "C-1.3", "C-1.4", "C-1.5", "C-1.6",
        "C-2.1", "C-2.2", "C-2.3", "C-2.4", "C-2.5", "C-2.6",
        "C-3.1", "C-3.2", "C-3.3", "C-3.4",
        "C-4.1", "C-4.2", "C-4.3", "C-4.4", "C-4.5", "C-4.6", "C-4.7",
        "C-5.1", "C-6.1", "C-7.1", "C-7.2", "C-7.3",
        "C-8.1", "C-8.2", "C-8.3", "C-8.4", "C-8.5", "C-8.6", "C-8.7", "C-8.8", "C-8.9", "C-8.10", "C-8.11", "C-8.12", "C-8.13",
        "C-9.1", "C-9.2", "C-9.3", "C-9.4", "C-9.5", "C-9.6", "C-9.7",
        "C-10.1", "C-10.2", "C-10.3", "C-10.4", "C-10.5", "C-10.6", "C-10.7", "C-10.8", "C-10.9",
        "C-11.1", "C-11.2",
        "C-12.1", "C-12.2", "C-12.3", "C-12.4", "C-12.5",
        "C-13.1", "C-13.2", "C-13.3", "C-13.4"
    ];

    // Combine actual data with the comprehensive list for display
    const displayCurricular = [...new Set([...curricular_goals, ...allCurricular])];
    const displayCompetencies = [...new Set([...competencies, ...allCompetencies])];

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.6', // Increased line-height for more vertical space
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
            '@media print': {
                margin: 0,
                padding: '15mm 20mm',
                boxShadow: 'none',
                width: '210mm',
                height: '297mm'
            }
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '80px solid #f1905b',
                    borderLeft: '80px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '80px solid #ed701e',
                    borderRight: '80px solid transparent'
                }}>
            </div>

            {/*/!* Orange corner decorations *!/*/}
            {/*<div className="absolute top-0 right-0 w-20 h-20 bg-orange-500"*/}
            {/*     style={{clipPath: 'polygon(100% 0, 0 0, 100% 100%)'}}/>*/}
            {/*<div className="absolute bottom-0 left-0 w-20 h-20 bg-orange-200"*/}
            {/*     style={{clipPath: 'polygon(0 100%, 0 0, 100% 100%)'}}/>*/}
            {/*<div className="absolute bottom-0 right-0 w-20 h-20 bg-orange-300"*/}
            {/*     style={{clipPath: 'polygon(100% 0, 0 100%, 100% 100%)'}}/>*/}

            {/* Page Title */}
            <h1 className="text-center text-orange-500 font-bold text-xl mt-2 mb-4">PART B</h1>

            {/* Domain Header */}
            <div className="bg-[#f89646] text-black py-1 px-4 text-center text-lg font-bold mb-2">
                {domainData.domain_name}
            </div>

            {/* Main Content Area */}
            <div className="border border-gray-300">
                {/* Curricular Goals Section */}
                <div className="border-b border-gray-300 p-2">
                    <div className="flex items-start">
                        <div>
                            <div className="font-bold">Curricular Goals</div>
                            <div className="text-xs text-gray-600">(Choose one or more)</div>
                        </div>
                        <div className="flex flex-wrap gap-x-8 gap-y-1 ml-6 mt-4">
                            {displayCurricular.slice(0, 10).map(goal => (
                                <CheckboxItem
                                    key={goal}
                                    label={goal}
                                    checked={curricular_goals.includes(goal)}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Competencies Section */}
                <div className="border-b border-gray-300 p-2">
                    <div className="flex items-start">
                        <div>
                            <div className="font-bold">Competencies</div>
                            <div className="text-xs text-gray-600">(Choose one or more)</div>
                        </div>
                        <div className="grid grid-cols-5 gap-x-8 gap-y-1 ml-6 mt-0.5">
                            {displayCompetencies.slice(0, 15).map(comp => (
                                <CheckboxItem
                                    key={comp}
                                    label={comp}
                                    checked={competencies.includes(comp)}
                                />
                            ))}
                        </div>
                    </div>
                </div>


                {/* Activity Section */}
                <div>
                    <div className="bg-[#f89646] text-black py-1 px-4 text-center font-bold">
                        ACTIVITY
                    </div>

                    <div className="p-2">
                        <div className="mb-1">
                            <div className="font-medium text-sm">Approach of the Activity: <span
                                className="font-normal text-xs">(Please ✓ all that apply)</span></div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                <CheckboxItem label="Art-integrated" />
                                <CheckboxItem label="Sports-integrated" />
                                <CheckboxItem label="Toy-based" />
                                <CheckboxItem label="Technology-integrated" />
                                <CheckboxItem label="Any Other" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="font-medium mb-1">Activity:</div>
                                <div className="border border-gray-300 h-60 p-2 overflow-y-auto bg-white text-xs">
                                    <ReactMarkdown>{activity}</ReactMarkdown>
                                </div>
                            </div>
                            <div>
                                <div className="font-medium text-xs mb-1">Assessment Question:</div>
                                <div className="border border-gray-300 h-60 p-2 overflow-y-auto bg-white text-xs">
                                    <ReactMarkdown>{assessment_questions_text}</ReactMarkdown>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Assessment Rubric Section */}
            <div className="mt-2">
                <div className="bg-[#f89646] text-black py-1 px-4 text-center font-bold">
                    ASSESSMENT RUBRIC
                </div>

                <table className="w-full border-collapse border border-gray-300 text-xs">
                    <thead>
                        <tr>
                            <th className="w-1/4 border border-gray-300"></th>
                            <th className="w-1/4 border border-gray-300 p-1 font-bold text-center">Beginner</th>
                            <th className="w-1/4 border border-gray-300 p-1 font-bold text-center">Proficient</th>
                            <th className="w-1/4 border border-gray-300 p-1 font-bold text-center">Advanced</th>
                        </tr>
                    </thead>
                    <tbody>
                        {assessment_rubric.length > 0 ? (
                            assessment_rubric.map((rubric, index) => (
                                <tr key={index}>
                                    <td className={`border border-gray-300 p-1 font-bold text-center ${index === 0 ? 'bg-[#ec99b6]' :
                                            index === 1 ? 'bg-[#faf8b4]' : 'bg-[#7dddff]'
                                        }`}>
                                        {rubric.criterion}
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        {rubric.stream || rubric.beginner || ''}
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        {rubric.mountain || rubric.proficient || ''}
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        {rubric.sky || rubric.advanced || ''}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            // Default rubric if no data available
                            <>
                                <tr>
                                    <td className="border border-gray-300 p-1 bg-[#ec99b6] font-bold text-center">
                                        Awareness
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Basic understanding with guidance
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Clear understanding with some guidance
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Strong independent understanding
                                    </td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-1 bg-[#faf8b4] font-bold text-center">
                                        Sensitivity
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Basic application with assistance
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Accurate application with minor errors
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Skillful and precise application
                                    </td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-1 bg-[#7dddff] font-bold text-center">
                                        Creativity
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Limited originality with support
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Some original ideas and clear communication
                                    </td>
                                    <td className="border border-gray-300 p-1 h-12 text-xs overflow-hidden">
                                        Displays originality and innovative ideas
                                    </td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>

                <p className="text-xs italic mt-1 text-gray-600">
                    *Note: Circle the relevant performance level based on the individual student's performance for each
                    ability for this activity.
                </p>
            </div>

            <div className="border-b-2 mt-2 border-[#fbd3b1] w-full mb-1" />
        </div>
    );
};

export default PartBDomainPage;